// src/components/WorkflowsTab.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Zap, Play, Pause, Edit3, Trash2, Plus, 
  ChevronRight, ExternalLink, Copy, Check,
  RefreshCw, Filter, Search, Download, Share2,
  Workflow, Clock, Users, Target, DollarSign,
  Mail, Calendar, MessageSquare, FileText, Database,
  Layers, Code, Settings, Cloud, GitBranch,
  CheckCircle, AlertCircle, Bell, UserPlus,
  TrendingUp, BarChart3, PieChart, ArrowRight,
  MoreVertical, X, ChevronDown, ChevronUp,
  Move, GripVertical, Link, GitMerge,
  Terminal, Cpu, Server, Globe, Smartphone,
  Lock, Unlock, Eye, EyeOff, List, Minus
} from 'lucide-react';

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  duration: string;
  connector?: string;
  connectorLogo?: React.ReactNode;
  type?: 'trigger' | 'action' | 'condition' | 'delay' | 'webhook';
  position?: { x: number; y: number };
}

interface Workflow {
  id: string;
  name: string;
  description: string;
  capability: string;
  steps: WorkflowStep[];
  lastRun: string;
  runCount: number;
  successRate: number;
  isActive: boolean;
  canCustomize: boolean;
  isCustom?: boolean;
  owner?: string;
  createdDate?: string;
}

const WorkflowsTab: React.FC = () => {
  const [workflows, setWorkflows] = useState<Workflow[]>([
    {
      id: 'wf-general-capacity-plan',
      name: 'Team Transition Workflow',
      description: 'Complete workflow for moving team members between projects',
      capability: 'general-capacity-plan',
      steps: [
        { id: '1', title: 'Request Initiation', description: 'Submit HR and manager approvals', duration: '2 days', connector: 'outlook', connectorLogo: <Mail size={16} /> },
        { id: '2', title: 'Knowledge Transfer Planning', description: 'Schedule sessions and document handover', duration: '3 days', connector: 'calendar', connectorLogo: <Calendar size={16} /> },
        { id: '3', title: 'System Updates', description: 'Update project assignments and permissions', duration: '1 day', connector: 'database', connectorLogo: <Database size={16} /> },
        { id: '4', title: 'Team Communication', description: 'Announce changes to both teams', duration: '1 day', connector: 'message', connectorLogo: <MessageSquare size={16} /> },
        { id: '5', title: 'Transition Phase', description: 'Overlap period and monitoring', duration: '2 weeks', connector: 'monitoring', connectorLogo: <BarChart3 size={16} /> },
        { id: '6', title: 'Performance Review', description: '30-day post-move assessment', duration: '30 days', connector: 'review', connectorLogo: <CheckCircle size={16} /> },
      ],
      lastRun: '2026-01-15',
      runCount: 42,
      successRate: 92,
      isActive: true,
      canCustomize: true
    },
    {
      id: 'wf-automate-root-cause-analysis',
      name: 'Project Bottleneck Resolution',
      description: 'Workflow to identify and resolve project bottlenecks',
      capability: 'automate-root-cause-analysis',
      steps: [
        { id: '1', title: 'Immediate Triage', description: 'Identify critical path blockers', duration: '1 day', connector: 'alert', connectorLogo: <AlertCircle size={16} /> },
        { id: '2', title: 'Resource Reallocation', description: 'Shift resources to high-priority tasks', duration: '2 days', connector: 'reallocation', connectorLogo: <RefreshCw size={16} /> },
        { id: '3', title: 'Process Adjustments', description: 'Implement daily standups and visibility', duration: '3 days', connector: 'process', connectorLogo: <Settings size={16} /> },
        { id: '4', title: 'Technical Resolution', description: 'Address technical debt and issues', duration: '1 week', connector: 'technical', connectorLogo: <Code size={16} /> },
        { id: '5', title: 'Monitoring Phase', description: 'Track progress and adjust as needed', duration: '2 weeks', connector: 'monitoring', connectorLogo: <TrendingUp size={16} /> },
      ],
      lastRun: '2026-01-14',
      runCount: 28,
      successRate: 88,
      isActive: true,
      canCustomize: true
    },
    {
      id: 'wf-match-internal-talent ',
      name: 'Initiative Launch Workflow',
      description: 'Complete workflow for launching new initiatives',
      capability: 'match-internal-talent ',
      steps: [
        { id: '1', title: 'Requirements Gathering', description: 'Define scope and objectives', duration: '2 weeks', connector: 'requirements', connectorLogo: <FileText size={16} /> },
        { id: '2', title: 'Team Assembly', description: 'Recruit and onboard team members', duration: '3 weeks', connector: 'team', connectorLogo: <UserPlus size={16} /> },
        { id: '3', title: 'Architecture Design', description: 'Design system architecture and tech stack', duration: '2 weeks', connector: 'design', connectorLogo: <Layers size={16} /> },
        { id: '4', title: 'Development Sprints', description: 'Agile development with 2-week sprints', duration: '12 weeks', connector: 'development', connectorLogo: <Code size={16} /> },
        { id: '5', title: 'Testing & QA', description: 'Comprehensive testing and bug fixing', duration: '4 weeks', connector: 'testing', connectorLogo: <CheckCircle size={16} /> },
      ],
      lastRun: '2026-01-10',
      runCount: 15,
      successRate: 85,
      isActive: true,
      canCustomize: true
    },
    {
      id: 'wf-optimize-it-budget',
      name: 'Budget Change Implementation',
      description: 'Workflow for implementing budget changes across departments',
      capability: 'optimize-it-budget',
      steps: [
        { id: '1', title: 'Cost Analysis', description: 'Identify cost-saving opportunities', duration: '1 week', connector: 'analysis', connectorLogo: <PieChart size={16} /> },
        { id: '2', title: 'Stakeholder Review', description: 'Review impact with department heads', duration: '3 days', connector: 'review', connectorLogo: <Users size={16} /> },
        { id: '3', title: 'Approval Process', description: 'Get executive approval for changes', duration: '2 days', connector: 'approval', connectorLogo: <CheckCircle size={16} /> },
        { id: '4', title: 'Implementation Planning', description: 'Plan phased implementation', duration: '1 week', connector: 'planning', connectorLogo: <Target size={16} /> },
        { id: '5', title: 'Communication', description: 'Communicate changes to teams', duration: '2 days', connector: 'communication', connectorLogo: <Bell size={16} /> },
      ],
      lastRun: '2026-01-12',
      runCount: 23,
      successRate: 90,
      isActive: true,
      canCustomize: true
    },
    {
      id: 'wf-ai-coding-impact',
      name: 'AI Coding Impact Analysis',
      description: 'Workflow for analyzing AI coding tool productivity and ROI impact',
      capability: 'ai-coding-impact',
      steps: [
        { id: '1', title: 'Token Usage Analysis', description: 'Analyze AI coding assistant token consumption patterns', duration: '2 days', connector: 'analysis', connectorLogo: <Code size={16} className="text-indigo-600" /> },
        { id: '2', title: 'Productivity Impact', description: 'Measure coding velocity and completion time improvements', duration: '3 days', connector: 'analysis', connectorLogo: <TrendingUp size={16} className="text-green-600" /> },
        { id: '3', title: 'Code Quality Analysis', description: 'Analyze defect rates, code review time, and complexity', duration: '2 days', connector: 'analysis', connectorLogo: <CheckCircle size={16} className="text-blue-600" /> },
        { id: '4', title: 'ROI Calculation', description: 'Calculate return on investment from AI coding tools', duration: '1 day', connector: 'analysis', connectorLogo: <DollarSign size={16} className="text-green-600" /> },
        { id: '5', title: 'Team Impact Assessment', description: 'Evaluate impact across skill levels and adoption rates', duration: '2 days', connector: 'analysis', connectorLogo: <Users size={16} className="text-purple-600" /> },
        { id: '6', title: 'Monitoring Setup', description: 'Set up ongoing dashboard for AI tool effectiveness', duration: '3 days', connector: 'monitoring', connectorLogo: <BarChart3 size={16} className="text-indigo-600" /> },
      ],
      lastRun: '2026-01-16',
      runCount: 18,
      successRate: 87,
      isActive: true,
      canCustomize: true
    },
    {
      id: 'wf-custom-1',
      name: 'Custom: Marketing Campaign Launch',
      description: 'Custom workflow for launching marketing campaigns',
      capability: 'general',
      steps: [
        { id: '1', title: 'Campaign Briefing', description: 'Define campaign goals and metrics', duration: '3 days', connector: 'briefing', connectorLogo: <FileText size={16} /> },
        { id: '2', title: 'Content Creation', description: 'Create marketing materials and content', duration: '1 week', connector: 'content', connectorLogo: <Edit3 size={16} /> },
        { id: '3', title: 'Channel Setup', description: 'Set up social media and advertising channels', duration: '2 days', connector: 'channels', connectorLogo: <Share2 size={16} /> },
        { id: '4', title: 'Launch Execution', description: 'Execute campaign across all channels', duration: '1 day', connector: 'launch', connectorLogo: <Zap size={16} /> },
      ],
      lastRun: '2026-01-13',
      runCount: 8,
      successRate: 95,
      isActive: true,
      canCustomize: true,
      isCustom: true,
      owner: 'John Doe',
      createdDate: '2026-01-01'
    },
  ]);

  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedWorkflow, setEditedWorkflow] = useState<Workflow | null>(null);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'flow' | 'list'>('flow');
  const [draggingNode, setDraggingNode] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [canvasPosition, setCanvasPosition] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(0.8);
  
  const canvasRef = useRef<HTMLDivElement>(null);

  // Auto-select first workflow on mount
  useEffect(() => {
    if (workflows.length > 0 && !selectedWorkflow) {
      setSelectedWorkflow(workflows[0]);
    }
  }, [workflows]);

  const capabilityIcons = {
    'general-capacity-plan': Users,
    'automate-root-cause-analysis': Zap,
    'match-internal-talent ': Target,
    'optimize-it-budget': DollarSign,
    'ai-coding-impact': Code, 
    'general': Workflow
  };

  const capabilityColors = {
    'general-capacity-plan': 'bg-blue-100 text-blue-800',
    'automate-root-cause-analysis': 'bg-orange-100 text-orange-800',
    'match-internal-talent ': 'bg-green-100 text-green-800',
    'optimize-it-budget': 'bg-purple-100 text-purple-800',
    'ai-coding-impact': 'bg-indigo-100 text-indigo-800',
    'general': 'bg-gray-100 text-gray-800'
  };

  const connectorLogos: Record<string, React.ReactNode> = {
    'outlook': <Mail size={16} className="text-blue-600" />,
    'calendar': <Calendar size={16} className="text-red-600" />,
    'message': <MessageSquare size={16} className="text-green-600" />,
    'database': <Database size={16} className="text-purple-600" />,
    'monitoring': <BarChart3 size={16} className="text-blue-500" />,
    'review': <CheckCircle size={16} className="text-green-600" />,
    'alert': <AlertCircle size={16} className="text-red-600" />,
    'reallocation': <RefreshCw size={16} className="text-blue-600" />,
    'process': <Settings size={16} className="text-gray-600" />,
    'technical': <Code size={16} className="text-indigo-600" />,
    'requirements': <FileText size={16} className="text-blue-600" />,
    'team': <UserPlus size={16} className="text-green-600" />,
    'design': <Layers size={16} className="text-purple-600" />,
    'development': <Code size={16} className="text-gray-800" />,
    'testing': <CheckCircle size={16} className="text-green-600" />,
    'analysis': <PieChart size={16} className="text-blue-600" />,
    'approval': <CheckCircle size={16} className="text-green-600" />,
    'planning': <Target size={16} className="text-red-600" />,
    'communication': <Bell size={16} className="text-yellow-600" />,
    'briefing': <FileText size={16} className="text-blue-600" />,
    'content': <Edit3 size={16} className="text-green-600" />,
    'channels': <Share2 size={16} className="text-purple-600" />,
    'launch': <Zap size={16} className="text-yellow-600" />,
  };

  const filteredWorkflows = workflows.filter(workflow => {
    if (filter !== 'all' && workflow.capability !== filter) return false;
    if (searchQuery && !workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !workflow.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getNodeTypeColor = (index: number) => {
    const colors = [
      'bg-gradient-to-r from-blue-500 to-cyan-500',
      'bg-gradient-to-r from-purple-500 to-pink-500',
      'bg-gradient-to-r from-green-500 to-emerald-500',
      'bg-gradient-to-r from-orange-500 to-red-500',
      'bg-gradient-to-r from-yellow-500 to-amber-500',
      'bg-gradient-to-r from-indigo-500 to-purple-500'
    ];
    return colors[index % colors.length];
  };

  const getCapabilityLabel = (capability: string) => {
    return capability.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getStepPositions = (steps: WorkflowStep[]) => {
    return steps.map((step, index) => {
      const baseY = 120;
      const x = 60 + index * 280;
      const y = baseY + Math.sin(index * 0.6) * 40;
      
      return {
        ...step,
        position: step.position || { x, y },
        type: step.type || (index === 0 ? 'trigger' : 'action')
      };
    });
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    const stepsWithPositions = getStepPositions(workflow.steps);
    setEditedWorkflow({...workflow, steps: stepsWithPositions});
    setIsEditing(true);
    setZoom(0.8);
    setCanvasPosition({ x: 50, y: 50 });
  };

  const handleSaveWorkflow = () => {
    if (!editedWorkflow) return;
    
    setWorkflows(prev => prev.map(w => 
      w.id === editedWorkflow.id ? {...editedWorkflow, isCustom: true} : w
    ));
    setSelectedWorkflow(editedWorkflow);
    setIsEditing(false);
    setEditedWorkflow(null);
  };

  const handleDeleteWorkflow = (id: string) => {
    if (confirm('Are you sure you want to delete this workflow?')) {
      setWorkflows(prev => prev.filter(w => w.id !== id));
      if (selectedWorkflow?.id === id) {
        setSelectedWorkflow(workflows.length > 1 ? workflows[1] : null);
      }
    }
  };

  const handleDuplicateWorkflow = (workflow: Workflow) => {
    const newWorkflow = {
      ...workflow,
      id: `wf-custom-${Date.now()}`,
      name: `${workflow.name} (Copy)`,
      isCustom: true,
      owner: 'You',
      createdDate: new Date().toISOString().split('T')[0],
      runCount: 0,
      lastRun: 'Never'
    };
    
    setWorkflows(prev => [...prev, newWorkflow]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateNewWorkflow = () => {
    const newWorkflow: Workflow = {
      id: `wf-new-${Date.now()}`,
      name: 'New Custom Workflow',
      description: 'Custom workflow created by user',
      capability: 'general',
      steps: [
        { 
          id: 'step-1', 
          title: 'Start', 
          description: 'First step description', 
          duration: '1 day',
          type: 'trigger'
        },
        { 
          id: 'step-2', 
          title: 'Action Step', 
          description: 'Second step description', 
          duration: '2 days',
          type: 'action'
        },
      ],
      lastRun: 'Never',
      runCount: 0,
      successRate: 0,
      isActive: false,
      canCustomize: true,
      isCustom: true,
      owner: 'You',
      createdDate: new Date().toISOString().split('T')[0]
    };
    
    setWorkflows(prev => [...prev, newWorkflow]);
    setSelectedWorkflow(newWorkflow);
    setEditedWorkflow({...newWorkflow});
    setIsEditing(true);
  };

  const handleToggleActive = (id: string) => {
    setWorkflows(prev => prev.map(w => 
      w.id === id ? {...w, isActive: !w.isActive} : w
    ));
  };

  const handleAddStep = () => {
    if (!editedWorkflow) return;
    
    const lastStep = editedWorkflow.steps[editedWorkflow.steps.length - 1];
    const lastPosition = lastStep.position || { x: 60 + (editedWorkflow.steps.length - 1) * 280, y: 120 };
    const newPosition = { 
      x: lastPosition.x + 280,
      y: lastPosition.y + Math.sin(editedWorkflow.steps.length * 0.6) * 40
    };
    
    const newStep: WorkflowStep = {
      id: `step-${Date.now()}`,
      title: 'New Step',
      description: 'Step description',
      duration: '1 day',
      type: 'action',
      position: newPosition
    };
    
    setEditedWorkflow({
      ...editedWorkflow,
      steps: [...editedWorkflow.steps, newStep]
    });
  };

  const handleUpdateStep = (stepId: string, field: string, value: string) => {
    if (!editedWorkflow) return;
    
    setEditedWorkflow({
      ...editedWorkflow,
      steps: editedWorkflow.steps.map(step => 
        step.id === stepId ? {...step, [field]: value} : step
      )
    });
  };

  const handleDeleteStep = (stepId: string) => {
    if (!editedWorkflow) return;
    
    setEditedWorkflow({
      ...editedWorkflow,
      steps: editedWorkflow.steps.filter(step => step.id !== stepId)
    });
  };

  const handleDragStart = (e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    setDraggingNode(nodeId);
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleDrag = (e: MouseEvent) => {
    if (!draggingNode || !editedWorkflow || !canvasRef.current) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = (e.clientX - canvasRect.left - dragOffset.x) / zoom + canvasPosition.x;
    const newY = (e.clientY - canvasRect.top - dragOffset.y) / zoom + canvasPosition.y;
    
    setEditedWorkflow({
      ...editedWorkflow,
      steps: editedWorkflow.steps.map(step => 
        step.id === draggingNode 
          ? { ...step, position: { x: newX, y: newY } }
          : step
      )
    });
  };

  const handleDragEnd = () => {
    setDraggingNode(null);
  };

  useEffect(() => {
    if (draggingNode) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [draggingNode, editedWorkflow, zoom, canvasPosition]);

  const renderFlowView = (workflow: Workflow, isEditMode: boolean = false) => {
  const steps = isEditMode ? workflow.steps : getStepPositions(workflow.steps);
  const nodeWidth = 192;
  const nodeHeight = 140;
  const horizontalSpacing = 280;
  
  return (
    <div 
      ref={canvasRef}
      className="relative flex-1 overflow-auto bg-gradient-to-br from-gray-50 to-gray-100"
      style={{ 
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 0, 0, 0.05) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        minHeight: '500px'
      }}
    >
      {/* Canvas Controls - HIGHER Z-INDEX */}
      <div className="absolute top-4 left-4 z-50 flex items-center space-x-2 bg-white rounded-lg shadow-lg p-2">
        <button
          onClick={() => setZoom(prev => Math.min(1.5, prev + 0.1))}
          className="p-2 hover:bg-gray-100 rounded"
        >
          <Plus size={16} />
        </button>
        <button
          onClick={() => setZoom(prev => Math.max(0.5, prev - 0.1))}
          className="p-2 hover:bg-gray-100 rounded"
        >
          <Minus size={16} />
        </button>
        <span className="text-sm text-gray-600 px-2">{Math.round(zoom * 100)}%</span>
        <div className="w-px h-6 bg-gray-300" />
        <button
          onClick={() => setCanvasPosition({ x: 50, y: 50 })}
          className="p-2 hover:bg-gray-100 rounded"
        >
          <RefreshCw size={16} />
        </button>
        {!isEditMode && (
          <button
            onClick={() => setViewMode('list')}
            className="p-2 hover:bg-gray-100 rounded"
          >
            <List size={16} />
          </button>
        )}
      </div>

      {/* Nodes Container with SVG overlay - LOWER Z-INDEX */}
      <div 
        className="relative z-20"
        style={{ 
          transform: `translate(${canvasPosition.x}px, ${canvasPosition.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          width: `${Math.max(steps.length * horizontalSpacing, 1000)}px`,
          height: '500px'
        }}
      >
        {/* Connection Lines */}
        <svg 
          className="absolute inset-0 pointer-events-none z-10"
          style={{ width: '100%', height: '100%' }}
        >
          {steps.map((step, index) => {
            const nextStep = steps[index + 1];
            if (!nextStep || !step.position || !nextStep.position) return null;
            
            const startX = step.position.x + nodeWidth - 6;
            const startY = step.position.y + (nodeHeight / 2);
            const endX = nextStep.position.x + 6;
            const endY = nextStep.position.y + (nodeHeight / 2);
            
            const horizontalDistance = endX - startX;
            const curveIntensity = horizontalDistance * 0.2;
            
            return (
              <g key={`edge-${step.id}-${nextStep.id}`}>
                {/* Main curved path */}
                <path
                  d={`M ${startX} ${startY} 
                      C ${startX + curveIntensity} ${startY}, 
                        ${endX - curveIntensity} ${endY}, 
                        ${endX} ${endY}`}
                  stroke="url(#gradient)"
                  strokeWidth="2.5"
                  fill="none"
                  markerEnd="url(#arrowhead)"
                  className="transition-all duration-300"
                />
                
                {/* Subtle glow effect */}
                <path
                  d={`M ${startX} ${startY} 
                      C ${startX + curveIntensity} ${startY}, 
                        ${endX - curveIntensity} ${endY}, 
                        ${endX} ${endY}`}
                  stroke="url(#glow)"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeOpacity="0.15"
                  className="blur-[2px]"
                />
              </g>
            );
          })}
          
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="50%" stopColor="#7C3AED" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
            
            <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
            
            {/* LARGER arrowhead */}
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="8"
              refX="9"
              refY="4"
              orient="auto"
            >
              <polygon 
                points="0 0, 10 4, 0 8" 
                fill="url(#gradient)"
              />
            </marker>
          </defs>
        </svg>

        {/* Flow Animation Dots */}
        <svg 
          className="absolute inset-0 pointer-events-none z-10"
          style={{ width: '100%', height: '100%' }}
        >
          {steps.map((step, index) => {
            const nextStep = steps[index + 1];
            if (!nextStep || !step.position || !nextStep.position) return null;
            
            const startX = step.position.x + nodeWidth - 6;
            const startY = step.position.y + (nodeHeight / 2);
            const endX = nextStep.position.x + 6;
            const endY = nextStep.position.y + (nodeHeight / 2);
            const horizontalDistance = endX - startX;
            const curveIntensity = horizontalDistance * 0.2;
            
            return (
              <circle
                key={`flow-${step.id}-${nextStep.id}`}
                r="2"
                fill="#3B82F6"
                opacity="0.7"
              >
                <animateMotion
                  dur="3s"
                  repeatCount="indefinite"
                  path={`M ${startX} ${startY} 
                         C ${startX + curveIntensity} ${startY}, 
                           ${endX - curveIntensity} ${endY}, 
                           ${endX} ${endY}`}
                />
              </circle>
            );
          })}
        </svg>

        {/* Nodes */}
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`absolute border-2 rounded-xl p-4 shadow-lg w-48 transition-all ${
              draggingNode === step.id ? 'shadow-2xl scale-105 z-40' : 'z-30 hover:shadow-xl'
            } border-blue-200 bg-white`}
            style={{
              left: step.position?.x || 0,
              top: step.position?.y || 0,
              cursor: isEditMode ? 'move' : 'default',
              height: `${nodeHeight}px`
            }}
            onMouseDown={(e) => isEditMode && step.position && handleDragStart(e, step.id)}
          >
            {/* Node Header */}
            <div className={`absolute -top-3 left-4 px-3 py-1 rounded-full text-white text-xs font-semibold flex items-center space-x-1 ${getNodeTypeColor(index)}`}>
              <span>{index === 0 ? 'START' : `STEP ${index + 1}`}</span>
            </div>
            
            {/* Node Content */}
            <div className="mb-3">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-gray-900 text-sm">{step.title}</h4>
                <div className="flex items-center space-x-1">
                  {step.connector && connectorLogos[step.connector]}
                  {isEditMode && <GripVertical size={12} className="text-gray-400" />}
                </div>
              </div>
              <p className="text-xs text-gray-600 mb-2 line-clamp-2">{step.description}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">{step.duration}</span>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-500">#{index + 1}</span>
                </div>
              </div>
            </div>
            
            {/* Node Ports */}
            <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 z-30">
              <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-2 border-white shadow"></div>
            </div>
            {index > 0 && (
              <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 z-30">
                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-2 border-white shadow"></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Node Button (only in edit mode) - HIGH Z-INDEX */}
      {isEditMode && (
        <div className="absolute bottom-4 right-4 z-50">
          <button
            onClick={handleAddStep}
            className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl transition-all"
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* Connection Style Legend - HIGH Z-INDEX */}
      {!isEditMode && (
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs z-50">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"></div>
            <span className="font-medium">Flow Direction</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span>Active Step</span>
          </div>
        </div>
      )}
    </div>
  );
};

  const renderListView = (workflow: Workflow) => {
    return (
      <div className="space-y-4 p-6">
        {workflow.steps.map((step, index) => (
          <div key={step.id} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start space-x-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${getNodeTypeColor(index)}`}>
                {index + 1}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <h5 className="font-semibold text-gray-900">{step.title}</h5>
                  </div>
                  <div className="flex items-center space-x-3">
                    {step.connector && (
                      <div className="flex items-center space-x-1 bg-gray-100 px-2 py-1 rounded-lg">
                        {connectorLogos[step.connector]}
                        <span className="text-sm text-gray-700 capitalize">
                          {step.connector.replace('-', ' ')}
                        </span>
                      </div>
                    )}
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {step.duration}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </div>
            {index < workflow.steps.length - 1 && (
              <div className="flex justify-center mt-4">
                <ArrowRight size={20} className="text-gray-300" />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderEditMode = () => {
    if (!editedWorkflow) return null;

    const steps = getStepPositions(editedWorkflow.steps);
    const nodeWidth = 192;
    const nodeHeight = 140;
    const horizontalSpacing = 280;

    return (
      <div className="h-full flex flex-col">
        {/* Fixed Header with Cancel/Save */}
        <div className="bg-white border-b shadow-sm px-6 py-4 z-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Edit3 size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Editing: {editedWorkflow.name}</h2>
                <p className="text-sm text-gray-600">Drag nodes to rearrange workflow steps</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedWorkflow(null);
                }}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2 transition-colors min-w-[100px]"
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSaveWorkflow}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 flex items-center space-x-2 transition-all shadow-md min-w-[100px]"
              >
                <Check size={18} />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Step Editor - Should be on top */}
          <div className="w-80 border-r bg-gray-50 p-4 overflow-y-auto z-40 relative">
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">Step Properties</h3>
              <div className="space-y-4">
                {editedWorkflow.steps.map((step, index) => (
                  <div key={step.id} className="bg-white rounded-lg border p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-7 h-7 rounded-md ${getNodeTypeColor(index)} flex items-center justify-center text-white text-xs font-bold`}>
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900">Step {index + 1}</span>
                      </div>
                      {index > 0 && (
                        <button
                          onClick={() => handleDeleteStep(step.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Title</label>
                        <input
                          type="text"
                          value={step.title}
                          onChange={(e) => handleUpdateStep(step.id, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          placeholder="Step title"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Description</label>
                        <textarea
                          value={step.description}
                          onChange={(e) => handleUpdateStep(step.id, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          placeholder="Step description"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Duration</label>
                          <input
                            type="text"
                            value={step.duration}
                            onChange={(e) => handleUpdateStep(step.id, 'duration', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            placeholder="e.g., 2 days"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Connector</label>
                          <select
                            value={step.connector || ''}
                            onChange={(e) => handleUpdateStep(step.id, 'connector', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          >
                            <option value="">None</option>
                            <option value="outlook">Outlook</option>
                            <option value="calendar">Calendar</option>
                            <option value="database">Database</option>
                            <option value="message">Message</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={handleAddStep}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus size={18} />
                  <span>Add New Step</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Canvas - Should be behind */}
          <div className="flex-1 overflow-hidden relative">
            {/* Flow View embedded directly */}
            <div 
              ref={canvasRef}
              className="w-full h-full overflow-auto bg-gradient-to-br from-gray-50 to-gray-100"
              style={{ 
                backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 0, 0, 0.05) 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }}
            >
              {/* Canvas Controls */}
              <div className="absolute top-4 left-4 z-50 flex items-center space-x-2 bg-white rounded-lg shadow-lg p-2">
                <button
                  onClick={() => setZoom(prev => Math.min(1.5, prev + 0.1))}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <Plus size={16} />
                </button>
                <button
                  onClick={() => setZoom(prev => Math.max(0.5, prev - 0.1))}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <Minus size={16} />
                </button>
                <span className="text-sm text-gray-600 px-2">{Math.round(zoom * 100)}%</span>
                <div className="w-px h-6 bg-gray-300" />
                <button
                  onClick={() => setCanvasPosition({ x: 50, y: 50 })}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <RefreshCw size={16} />
                </button>
              </div>

              {/* Nodes Container with SVG overlay */}
              <div 
                className="relative"
                style={{ 
                  transform: `translate(${canvasPosition.x}px, ${canvasPosition.y}px) scale(${zoom})`,
                  transformOrigin: '0 0',
                  width: `${Math.max(steps.length * horizontalSpacing, 1000)}px`,
                  height: '500px'
                }}
              >
                {/* Connection Lines */}
                <svg 
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: '100%', height: '100%' }}
                >
                  {steps.map((step, index) => {
                    const nextStep = steps[index + 1];
                    if (!nextStep || !step.position || !nextStep.position) return null;
                    
                    const startX = step.position.x + nodeWidth - 6;
                    const startY = step.position.y + (nodeHeight / 2);
                    const endX = nextStep.position.x + 6;
                    const endY = nextStep.position.y + (nodeHeight / 2);
                    
                    const horizontalDistance = endX - startX;
                    const curveIntensity = horizontalDistance * 0.2;
                    
                    return (
                      <g key={`edge-${step.id}-${nextStep.id}`}>
                        {/* Main curved path */}
                        <path
                          d={`M ${startX} ${startY} 
                              C ${startX + curveIntensity} ${startY}, 
                                ${endX - curveIntensity} ${endY}, 
                                ${endX} ${endY}`}
                          stroke="url(#gradient)"
                          strokeWidth="2.5"
                          fill="none"
                          markerEnd="url(#arrowhead)"
                          className="transition-all duration-300"
                        />
                        
                        {/* Subtle glow effect */}
                        <path
                          d={`M ${startX} ${startY} 
                              C ${startX + curveIntensity} ${startY}, 
                                ${endX - curveIntensity} ${endY}, 
                                ${endX} ${endY}`}
                          stroke="url(#glow)"
                          strokeWidth="6"
                          fill="none"
                          strokeLinecap="round"
                          strokeOpacity="0.15"
                          className="blur-[2px]"
                        />
                      </g>
                    );
                  })}
                  
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="50%" stopColor="#7C3AED" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                    
                    <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                    
                    {/* LARGER arrowhead */}
                    <marker
                      id="arrowhead"
                      markerWidth="10"
                      markerHeight="8"
                      refX="9"
                      refY="4"
                      orient="auto"
                    >
                      <polygon 
                        points="0 0, 10 4, 0 8" 
                        fill="url(#gradient)"
                      />
                    </marker>
                  </defs>
                </svg>

                {/* Flow Animation Dots */}
                <svg 
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: '100%', height: '100%' }}
                >
                  {steps.map((step, index) => {
                    const nextStep = steps[index + 1];
                    if (!nextStep || !step.position || !nextStep.position) return null;
                    
                    const startX = step.position.x + nodeWidth - 6;
                    const startY = step.position.y + (nodeHeight / 2);
                    const endX = nextStep.position.x + 6;
                    const endY = nextStep.position.y + (nodeHeight / 2);
                    const horizontalDistance = endX - startX;
                    const curveIntensity = horizontalDistance * 0.2;
                    
                    return (
                      <circle
                        key={`flow-${step.id}-${nextStep.id}`}
                        r="2"
                        fill="#3B82F6"
                        opacity="0.7"
                      >
                        <animateMotion
                          dur="3s"
                          repeatCount="indefinite"
                          path={`M ${startX} ${startY} 
                                C ${startX + curveIntensity} ${startY}, 
                                  ${endX - curveIntensity} ${endY}, 
                                  ${endX} ${endY}`}
                        />
                      </circle>
                    );
                  })}
                </svg>

                {/* Nodes */}
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`absolute border-2 rounded-xl p-4 shadow-lg w-48 transition-all ${
                      draggingNode === step.id ? 'shadow-2xl scale-105 z-40' : 'z-30 hover:shadow-xl'
                    } border-blue-200 bg-white`}
                    style={{
                      left: step.position?.x || 0,
                      top: step.position?.y || 0,
                      cursor: 'move',
                      height: `${nodeHeight}px`
                    }}
                    onMouseDown={(e) => step.position && handleDragStart(e, step.id)}
                  >
                    {/* Node Header */}
                    <div className={`absolute -top-3 left-4 px-3 py-1 rounded-full text-white text-xs font-semibold flex items-center space-x-1 ${getNodeTypeColor(index)}`}>
                      <span>{index === 0 ? 'START' : `STEP ${index + 1}`}</span>
                    </div>
                    
                    {/* Node Content */}
                    <div className="mb-3">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{step.title}</h4>
                        <div className="flex items-center space-x-1">
                          {step.connector && connectorLogos[step.connector]}
                          <GripVertical size={12} className="text-gray-400" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-2 line-clamp-2">{step.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{step.duration}</span>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-gray-500">#{index + 1}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Node Ports */}
                    <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 z-30">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-2 border-white shadow"></div>
                    </div>
                    {index > 0 && (
                      <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 z-30">
                        <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-2 border-white shadow"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Node Button */}
              <div className="absolute bottom-4 right-4 z-50">
                <button
                  onClick={handleAddStep}
                  className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl transition-all"
                >
                  <Plus size={24} />
                </button>
              </div>
            </div>

            {/* Help Text - On top of everything */}
            <div className="absolute bottom-6 left-6 bg-white/95 rounded-lg shadow-lg p-4 max-w-xs z-50">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Move size={16} className="mr-2 text-blue-600" />
                Edit Mode Tips
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Drag nodes to rearrange</li>
                <li>• Edit step details on the left</li>
                <li>• Click "Add New Step" to extend workflow</li>
                <li>• Click Cancel or Save above when done</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <GitMerge className="mr-3" size={28} />
              Workflow Automation
            </h2>
            <p className="text-gray-600 mt-1">
              Visual workflow builder with drag & drop interface
            </p>
          </div>
          <button
            onClick={handleCreateNewWorkflow}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 transition-all flex items-center space-x-2 shadow-sm"
          >
            <Plus size={18} />
            <span>Create New Workflow</span>
          </button>
        </div>

        {/* Filters and Search */}
        <div className="mt-6 flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search workflows by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg border transition-colors ${filter === 'all' ? 'bg-blue-100 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              All Workflows
            </button>
            {Object.entries(capabilityIcons).map(([cap, Icon]) => (
              <button
                key={cap}
                onClick={() => setFilter(cap)}
                className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${filter === cap ? 'bg-blue-100 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                <Icon size={16} />
                <span>{getCapabilityLabel(cap)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Workflow List */}
        <div className={`${
          (selectedWorkflow && !isEditing) || (isEditing && editedWorkflow) 
            ? 'w-80' 
            : 'w-full'
        } border-r bg-gray-50 overflow-y-auto transition-all duration-300`}>
          <div className="p-4 space-y-3">
            {filteredWorkflows.map(workflow => {
              const Icon = capabilityIcons[workflow.capability as keyof typeof capabilityIcons] || Workflow;
              
              return (
                <div
                  key={workflow.id}
                  onClick={() => {
                    if (!isEditing) {
                      setSelectedWorkflow(workflow);
                    }
                  }}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedWorkflow?.id === workflow.id 
                      ? 'border-purple-400 shadow-md ring-2 ring-purple-100' 
                      : 'border-gray-200'
                  } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${capabilityColors[workflow.capability as keyof typeof capabilityColors]} bg-opacity-20`}>
                        <Icon size={18} />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900 text-sm">{workflow.name}</h3>
                          {workflow.isCustom && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                              Custom
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{workflow.description}</p>
                        <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
                          <span className="flex items-center space-x-1">
                            <div className={`w-2 h-2 rounded-full ${workflow.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <span>{workflow.isActive ? 'Active' : 'Inactive'}</span>
                          </span>
                          <span>•</span>
                          <span>{workflow.steps.length} steps</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </div>
                  
                  {/* Flow Preview */}
                  <div className="mt-3 flex items-center justify-center">
                    <div className="relative h-8 w-full bg-gray-100 rounded overflow-hidden">
                      {workflow.steps.slice(0, 4).map((step, index) => (
                        <div
                          key={index}
                          className={`absolute h-full ${getNodeTypeColor(index)}`}
                          style={{
                            left: `${(index / Math.min(workflow.steps.length, 4)) * 100}%`,
                            width: `${100 / Math.min(workflow.steps.length, 4)}%`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Workflow Detail View */}
        <div className="flex-1 overflow-hidden">
          {isEditing && editedWorkflow ? (
            // Edit Mode
            renderEditMode()
          ) : selectedWorkflow && !isEditing ? (
            // View Mode
            <div className="h-full flex flex-col bg-white">
              {/* Toolbar */}
              <div className="border-b px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${capabilityColors[selectedWorkflow.capability as keyof typeof capabilityColors]}`}>
                        {getCapabilityLabel(selectedWorkflow.capability)}
                      </span>
                      {selectedWorkflow.isCustom && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                          Custom Workflow
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedWorkflow.name}</h3>
                    <p className="text-gray-600">{selectedWorkflow.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* View Toggle */}
                  <div className="flex border border-gray-300 rounded-lg">
                    <button
                      onClick={() => setViewMode('flow')}
                      className={`px-3 py-1.5 rounded-l-lg ${viewMode === 'flow' ? 'bg-blue-500 text-white' : 'bg-gray-50 text-gray-700'}`}
                    >
                      <GitMerge size={16} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1.5 rounded-r-lg ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-gray-50 text-gray-700'}`}
                    >
                      <List size={16} />
                    </button>
                  </div>

                  <button
                    onClick={() => handleDuplicateWorkflow(selectedWorkflow)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1.5"
                  >
                    {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    <span>{copied ? 'Copied' : 'Duplicate'}</span>
                  </button>
                  <button
                    onClick={() => handleEditWorkflow(selectedWorkflow)}
                    className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-1.5"
                  >
                    <Edit3 size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleToggleActive(selectedWorkflow.id)}
                    className={`px-3 py-1.5 rounded-lg flex items-center space-x-1.5 ${
                      selectedWorkflow.isActive 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {selectedWorkflow.isActive ? <Pause size={16} /> : <Play size={16} />}
                    <span>{selectedWorkflow.isActive ? 'Pause' : 'Activate'}</span>
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="border-b px-6 py-4 bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-2xl font-bold text-gray-900">{selectedWorkflow.runCount}</p>
                    <p className="text-sm text-gray-600">Total Runs</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-2xl font-bold text-green-600">{selectedWorkflow.successRate}%</p>
                    <p className="text-sm text-gray-600">Success Rate</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-2xl font-bold text-gray-900">{selectedWorkflow.lastRun}</p>
                    <p className="text-sm text-gray-600">Last Run</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedWorkflow.steps.length}
                    </p>
                    <p className="text-sm text-gray-600">Steps</p>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-auto">
                {viewMode === 'flow' ? renderFlowView(selectedWorkflow) : renderListView(selectedWorkflow)}
              </div>

              {/* Footer */}
              <div className="border-t px-6 py-4 bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {selectedWorkflow.isCustom && selectedWorkflow.owner && (
                    <p>Created by {selectedWorkflow.owner} on {selectedWorkflow.createdDate}</p>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleDeleteWorkflow(selectedWorkflow.id)}
                    className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 flex items-center space-x-2"
                  >
                    <Trash2 size={16} />
                    <span>Delete Workflow</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Empty State
            <div className="h-full flex flex-col items-center justify-center p-12 bg-white">
              <div className="w-24 h-24 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-6">
                <GitMerge size={40} className="text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Workflow Selected</h3>
              <p className="text-gray-600 text-center max-w-md mb-6">
                Select a workflow from the list to view its visual flow, or create a new custom workflow.
              </p>
              <button
                onClick={handleCreateNewWorkflow}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 flex items-center space-x-2"
              >
                <Plus size={18} />
                <span>Create Your First Workflow</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowsTab;